// Picks: the list of BC warehouse and inventory picks at this location, and working one in walking order. Each
// line is go to the bin, scan it, scan the item (and lot or serial), confirm how many. Short picks need a reason.
// Register sends what was picked to Business Central; until then it's kept in Courage, so nothing is lost.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { wms, type Content, type LineScan, type PickDoc, type PickStep, type PickSummary } from "./wmsApi";
import { Alert, Done, Header, I, LocChip, ScanField, Stepper, feedback, fmt, errText, errBody, getLocation } from "./ui";

const due = (d: string | null) => {
  if (!d) return "";
  const t = new Date().toISOString().slice(0, 10);
  if (d < t) return "Late";
  if (d === t) return "Ships today";
  return `Ships ${new Date(d + "T12:00:00").toLocaleDateString([], { weekday: "short" })}`;
};

export function PickListPage({ loc }: { loc: string }) {
  const nav = useNavigate();
  const [tab, setTab] = useState<"mine" | "unassigned">("mine");
  const [data, setData] = useState<{ mine: PickSummary[]; unassigned: PickSummary[]; others: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [scanErr, setScanErr] = useState<string | null>(null);
  useEffect(() => {
    wms.picks(loc).then((d) => { setData(d); if (!d.mine.length && d.unassigned.length) setTab("unassigned"); }).catch((e) => setErr(errText(e)));
  }, [loc]);
  const scan = async (code: string) => {
    setScanErr(null);
    try {
      const r = await wms.scan(loc, code);
      if (r.kind === "pick") { feedback.ok(); nav(`/wms/picks/${r.type}/${encodeURIComponent(r.no)}`); return; }
      feedback.bad(); setScanErr(`${code} isn't an open pick at ${loc}, or an order with one.`);
    } catch (e) { feedback.bad(); setScanErr(errText(e)); }
  };
  const list = data?.[tab] ?? [];
  return (
    <>
      <Header title="Picks" back="/wms" right={<LocChip code={loc} />} />
      <div className="w-tabs" role="tablist" aria-label="Which picks">
        <button type="button" role="tab" aria-selected={tab === "mine"} onClick={() => setTab("mine")}>Mine{data ? ` · ${data.mine.length}` : ""}</button>
        <button type="button" role="tab" aria-selected={tab === "unassigned"} onClick={() => setTab("unassigned")}>Unassigned{data ? ` · ${data.unassigned.length}` : ""}</button>
      </div>
      <div className="w-body">
        <ScanField label="Scan a pick sheet or order" placeholder="Pick or order number" mono={false} onScan={scan} />
        {scanErr && <Alert title="No pick for that">{scanErr}</Alert>}
        {err && <Alert title="Couldn't load picks">{err}</Alert>}
        {!data && !err && <p className="w-muted">Loading picks…</p>}
        {data && !list.length && <div className="w-card w-empty">{tab === "mine" ? "Nothing is assigned to you. Take one from Unassigned." : "No unassigned picks right now."}{data.others > 0 && ` ${data.others} more ${data.others === 1 ? "is" : "are"} assigned to other people.`}</div>}
        {list.map((p) => (
          <Link key={`${p.type}-${p.no}`} to={`/wms/picks/${p.type}/${encodeURIComponent(p.no)}`} className={`w-pickcard ${p.done > 0 ? "started" : ""}`}>
            <span className="w-label-row"><span className="w-strong big">{p.no}</span><span className={p.done > 0 ? "w-link" : "w-muted"}>{p.done > 0 ? "In progress" : p.type === "invt-pick" ? "Inventory pick" : "Not started"}</span></span>
            <span className="w-row-main"><span>{[p.sources.join(", "), p.destination].filter(Boolean).join(" · ")}</span>
              <span className="w-muted">{[due(p.dueDate), `${p.lines} line${p.lines === 1 ? "" : "s"}`, p.startBin && `starts at ${p.startBin}`].filter(Boolean).join(" · ")}</span></span>
            {p.done > 0 && <span className="w-progress"><span className="w-bar"><span style={{ width: `${Math.round((p.done / p.lines) * 100)}%` }} /></span><span className="w-small w-strong">{p.done} of {p.lines}</span></span>}
          </Link>
        ))}
      </div>
    </>
  );
}

type Phase = "bin" | "item" | "lot" | "qty" | "short" | "lines" | "registered" | "failed";

export function PickPage() {
  const { type = "", no = "" } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState<PickDoc | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [at, setAt] = useState<number | null>(null); // the line being picked
  const [phase, setPhase] = useState<Phase>("bin");
  const [wrong, setWrong] = useState<Extract<LineScan, { ok: false }> | null>(null);
  const [takeFrom, setTakeFrom] = useState<string | null>(null);
  const [binRows, setBinRows] = useState<Content[]>([]);
  const [lot, setLot] = useState<string | null>(null);
  const [serial, setSerial] = useState<string | null>(null);
  const [qty, setQty] = useState(0);
  const [reason, setReason] = useState("empty_bin");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reg, setReg] = useState<{ registered: number; short: number; remaining: number } | null>(null);
  const [regErr, setRegErr] = useState<{ error: string; bcMessage?: string } | null>(null);

  const load = async (jumpTo?: number | null) => {
    try {
      const d = await wms.pick(type, no);
      setDoc(d); setLoadErr(null);
      const next = jumpTo ?? d.steps.find((s) => s.status === "todo" || s.status === "partial")?.lineNo ?? null;
      if (next === null) { setAt(null); setPhase("lines"); } else start(d, next);
      return d;
    } catch (e) { setLoadErr(errText(e)); return null; }
  };
  useEffect(() => { load(); }, [type, no]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = doc?.steps.find((s) => s.lineNo === at) ?? null;
  const idx = doc && step ? doc.steps.indexOf(step) : -1;
  const fromBin = takeFrom ?? step?.binCode ?? "";
  const tracking: "lot" | "serial" | null = !step ? null : step.serialNo ? "serial" : step.lotNo ? "lot"
    : binRows.some((r) => r.itemNo === step.itemNo && r.serialNo) ? "serial" : binRows.some((r) => r.itemNo === step.itemNo && r.lotNo) ? "lot" : null;

  function start(d: PickDoc, lineNo: number) {
    const s = d.steps.find((x) => x.lineNo === lineNo)!;
    setAt(lineNo); setPhase("bin"); setWrong(null); setErr(null); setTakeFrom(null); setLot(null); setSerial(null); setQty(s.qty);
    fetchBin(d.location, s.binCode);
  }
  const fetchBin = (location: string, bin: string) => wms.bin(location, bin).then((r) => setBinRows(r.contents)).catch(() => setBinRows([]));

  async function scan(kind: "bin" | "item" | "lot" | "serial", code: string) {
    if (!step || !doc) return;
    setBusy(true); setErr(null); setWrong(null);
    try {
      const r = await wms.scanLine(type, no, step.lineNo, kind, code);
      if (!r.ok) { feedback.bad(); if (kind === "bin") setWrong(r); else setErr(r.error); return; }
      feedback.ok();
      if (kind === "bin") setPhase("item");
      else if (kind === "item") setPhase(tracking ? "lot" : "qty");
      else { if (kind === "lot") setLot(r.lotNo ?? null); else { setSerial(r.serialNo ?? null); setQty(1); } setPhase("qty"); }
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  }
  function takeOther(bin: string) {
    if (!doc) return;
    feedback.ok(); setTakeFrom(bin); setWrong(null); setPhase("item"); fetchBin(doc.location, bin);
  }

  async function save(b: { picked: number; shortReason?: string | null; skipped?: boolean }) {
    if (!step || !doc) return;
    setBusy(true); setErr(null);
    try {
      await wms.saveLine(type, no, step.lineNo, { picked: b.picked, lotNo: lot, serialNo: serial, binCode: takeFrom, shortReason: b.shortReason ?? null, skipped: b.skipped });
      b.skipped ? feedback.ok() : feedback.done();
      const d = await wms.pick(type, no);
      setDoc(d);
      const after = [...d.steps.slice(idx + 1), ...d.steps.slice(0, idx + 1)].find((s) => s.status === "todo" && s.lineNo !== step.lineNo);
      if (after) start(d, after.lineNo); else { setAt(null); setPhase("lines"); }
    } catch (e) {
      feedback.bad();
      const body = errBody(e);
      setErr(errText(e));
      if (body.code === "tracking_required") setPhase("lot");
    } finally { setBusy(false); }
  }

  async function register() {
    setBusy(true); setRegErr(null);
    try {
      const r = await wms.register(type, no);
      feedback.done(); setReg(r); setPhase("registered");
    } catch (e) {
      feedback.bad(); const body = errBody(e);
      setRegErr({ error: errText(e), bcMessage: body.bcMessage }); setPhase("failed");
    } finally { setBusy(false); }
  }

  const counts = useMemo(() => {
    const s = doc?.steps ?? [];
    return { full: s.filter((x) => x.status === "picked").length, short: s.filter((x) => x.status === "short" || x.status === "partial").length, todo: s.filter((x) => x.status === "todo").length, skipped: s.filter((x) => x.status === "skipped").length, picked: s.filter((x) => x.picked > 0).length };
  }, [doc]);
  const nextStep = doc && step ? [...doc.steps.slice(idx + 1), ...doc.steps.slice(0, idx)].find((s) => s.status === "todo") : null;
  const loc = doc?.location || getLocation();

  if (loadErr) return (
    <>
      <Header title={no} back="/wms/picks" />
      <div className="w-body"><Alert title="Can't open this pick">{loadErr}</Alert><Link to="/wms/picks" className="w-btn">Back to picks</Link></div>
    </>
  );
  if (!doc) return <><Header title={no} back="/wms/picks" /><div className="w-body"><p className="w-muted">Opening {no}…</p></div></>;

  const header = (sub?: string) => (
    <>
      <Header title={no} sub={sub ?? [doc.sources.join(", "), doc.destination].filter(Boolean).join(" · ")} back={phase === "lines" && step ? () => setPhase("bin") : "/wms/picks"}
        right={phase !== "lines" && phase !== "registered" && phase !== "failed" ? <button type="button" className="w-smallbtn" onClick={() => setPhase("lines")}>Lines</button> : <LocChip code={loc} />} />
      {step && !["lines", "registered", "failed"].includes(phase) && (
        <div className="w-progress-head">
          <div className="w-steps tight" style={{ gridTemplateColumns: `repeat(${doc.steps.length}, minmax(0, 1fr))` }}>
            {doc.steps.map((s) => <span key={s.lineNo} className={s.lineNo === step.lineNo ? "now" : s.status !== "todo" ? "on" : ""} />)}
          </div>
          <div className="w-small">Line {idx + 1} of {doc.steps.length}</div>
        </div>
      )}
    </>
  );

  // ---------- after register ----------
  if (phase === "registered" && reg) return (
    <>
      {header("Registered")}
      <div className="w-body center">
        <span className="w-bigtick"><I n="check" s={60} w={2.6} /></span>
        <h1 className="w-h1">Registered {reg.registered} line{reg.registered === 1 ? "" : "s"}</h1>
        <p className="w-muted">{doc.type === "invt-pick" ? "Posted in Business Central." : "Registered in Business Central."}{reg.remaining ? ` ${reg.remaining} line${reg.remaining === 1 ? " stays" : "s stay"} open for a later pick.` : ""}</p>
      </div>
      <div className="w-foot">
        {reg.remaining > 0 && <button type="button" className="w-btn" onClick={() => { setReg(null); load(); }}>Pick what's left</button>}
        <button type="button" className="w-btn primary" onClick={() => nav("/wms/picks")}>Back to picks</button>
      </div>
    </>
  );

  if (phase === "failed" && regErr) return (
    <>
      {header("Not registered yet")}
      <div className="w-body">
        <Alert title="Business Central said no">{regErr.error}</Alert>
        <div className="w-card w-confirm"><span className="w-tick"><I n="check" s={18} w={2.5} /></span><span>Your picks are saved. Nothing needs to be scanned again.</span></div>
        <div className="w-label">What you can do</div>
        <button type="button" className="w-row-btn" onClick={() => setPhase("lines")}>
          <span className="w-row-main"><span className="w-strong">Change a line</span><span className="w-muted">Pick it short, or from another lot or bin</span></span><I n="chev" />
        </button>
        {regErr.bcMessage && (
          <details className="w-details"><summary>Business Central's message</summary><code>{regErr.bcMessage}</code></details>
        )}
      </div>
      <div className="w-foot"><button type="button" className="w-btn primary" onClick={register} disabled={busy}>{busy ? "Trying…" : "Try again"}</button></div>
    </>
  );

  // ---------- all lines ----------
  if (phase === "lines" || !step) return (
    <>
      {header(counts.todo ? `${counts.todo} line${counts.todo === 1 ? "" : "s"} to go` : "All lines visited")}
      <div className="w-body">
        <div className="w-two">
          <div className="w-stat"><span className="w-small">Picked in full</span><span className="w-statn">{counts.full}</span></div>
          <div className="w-stat"><span className="w-small">Short or skipped</span><span className={`w-statn ${counts.short + counts.skipped ? "warn" : ""}`}>{counts.short + counts.skipped}</span></div>
        </div>
        <div className="w-card w-rows">
          {doc.steps.map((s) => (
            <button key={s.lineNo} type="button" className={`w-line link ${s.status === "short" || s.status === "partial" ? "warn" : ""}`} onClick={() => start(doc, s.lineNo)}>
              <span className="w-mono w-muted w-binc">{s.takeFromBin ?? s.binCode}</span>
              <span className="w-row-main"><span className="w-strong">{s.itemNo}</span>
                {(s.shortReason || s.skipped) && <span className="w-small">{s.skipped && !s.picked ? "Skipped" : doc.shortReasons[s.shortReason ?? ""] ?? "Short"}</span>}</span>
              <span className={s.status === "todo" || s.status === "skipped" ? "w-muted" : "w-strong"}>{fmt(s.picked)}/{fmt(s.qty)}</span>
            </button>
          ))}
        </div>
        {counts.short + counts.skipped > 0 && <p className="w-small">What isn't picked stays open on {doc.sources.join(", ") || no} for a later pick.</p>}
      </div>
      <div className="w-foot">
        {counts.todo > 0 && <button type="button" className="w-btn" onClick={() => { const n = doc.steps.find((s) => s.status === "todo"); if (n) start(doc, n.lineNo); }}>Back to picking</button>}
        <button type="button" className="w-btn primary" onClick={register} disabled={busy || !counts.picked}>{busy ? "Registering…" : counts.todo ? "Register what's picked so far" : "Register pick in BC"}</button>
      </div>
    </>
  );

  // ---------- one line ----------
  const unit = step.uom;
  const goCard = (
    <div className="w-go-card">
      <span className="w-go-l">Go to bin</span>
      <span className="w-go-c">{fromBin}</span>
      <span className="w-go-s">{phase === "bin" ? `${step.itemNo} · take ${fmt(step.qty)} ${unit}` : step.description}</span>
    </div>
  );
  const itemCard = (
    <div className="w-card w-item col">
      <div className="w-item-top"><span className="w-item-i"><I n="box" s={28} w={1.8} /></span>
        <span className="w-row-main"><span className="w-strong big">{step.itemNo}</span><span className="w-muted">{step.description}</span></span></div>
      <div className="w-two">
        <div className="w-stat soft"><span className="w-small">Take</span><span className="w-statn">{fmt(step.qty)} <small>{unit}</small></span></div>
        <div className="w-stat soft"><span className="w-small">{step.serialNo || tracking === "serial" ? "Serial" : "Lot"}</span><span className="w-strong">{step.lotNo || step.serialNo || (tracking ? "Scan at bin" : "None")}</span></div>
      </div>
    </div>
  );
  const checks = (
    <div className="w-card w-rows">
      <Done label="Bin" value={fromBin} mono />
      {phase !== "item" && <Done label="Item" value={step.itemNo} />}
      {(lot || serial) && <Done label={lot ? "Lot" : "Serial"} value={(lot ?? serial)!} mono />}
    </div>
  );

  return (
    <>
      {header()}
      {phase === "bin" && (
        <>
          <div className="w-body">
            {goCard}
            {wrong ? (
              <>
                <Alert title="Wrong bin">{wrong.error}</Alert>
                {wrong.alternative && (
                  <button type="button" className="w-row-btn" onClick={() => takeOther(wrong.alternative!.bin)}>
                    <span className="w-row-main"><span className="w-strong">Take it from <span className="w-mono">{wrong.alternative.bin}</span> instead</span><span className="w-muted">{wrong.alternative.bin} has {fmt(wrong.alternative.qty)} of {step.itemNo}</span></span><I n="chev" />
                  </button>
                )}
              </>
            ) : itemCard}
            {!wrong && nextStep && <div className="w-small">Next: <span className="w-mono">{nextStep.binCode}</span> · {nextStep.itemNo} · {fmt(nextStep.qty)} {nextStep.uom}</div>}
            {err && <Alert title="Try again">{err}</Alert>}
          </div>
          <div className="w-foot">
            <ScanField label={`Scan bin ${fromBin}`} placeholder={wrong ? "Scan again" : "Scan or type"} onScan={(c) => scan("bin", c)} busy={busy} />
            <div className="w-two">
              <button type="button" className="w-btn" onClick={() => save({ picked: 0, skipped: true })} disabled={busy}>Skip for now</button>
              <button type="button" className="w-btn" onClick={() => { setQty(0); setPhase("short"); }}>Can't pick all</button>
            </div>
          </div>
        </>
      )}
      {phase === "item" && (
        <>
          <div className="w-body">{checks}{itemCard}{err && <Alert title="Wrong item">{err}</Alert>}</div>
          <div className="w-foot"><ScanField label={`Scan the item: ${step.itemNo}`} onScan={(c) => scan("item", c)} busy={busy} /></div>
        </>
      )}
      {phase === "lot" && (
        <>
          <div className="w-body">
            {checks}
            {step.lotNo || step.serialNo ? <p className="w-muted">This line is for {step.lotNo ? `lot ${step.lotNo}` : `serial ${step.serialNo}`}. Scan it to confirm.</p>
              : <p className="w-muted">Scan the {tracking === "serial" ? "serial number" : "lot number"} on the {step.itemNo} you're taking.</p>}
            {err && <Alert title="Not that one">{err}</Alert>}
          </div>
          <div className="w-foot"><ScanField label={tracking === "serial" ? "Scan the serial number" : "Scan the lot"} onScan={(c) => scan(tracking === "serial" ? "serial" : "lot", c)} busy={busy} /></div>
        </>
      )}
      {phase === "qty" && (
        <>
          <div className="w-body">
            {checks}
            <div className="w-card w-qtycard">
              <label htmlFor="pick-qty" className="w-label">Quantity picked</label>
              <Stepper id="pick-qty" value={qty} onChange={setQty} max={serial ? 1 : step.qty} of={`of ${fmt(step.qty)}`} big />
              {lot && <span className="w-small">Lot {lot} has {fmt(binRows.filter((r) => r.itemNo === step.itemNo && r.lotNo === lot).reduce((a, r) => a + r.qty, 0))} here.</span>}
            </div>
            {err && <Alert title="Not saved">{err}</Alert>}
          </div>
          <div className="w-foot">
            <button type="button" className="w-btn primary" disabled={busy || !(qty > 0)} onClick={() => (qty < step.qty ? setPhase("short") : save({ picked: qty }))}>
              {qty < step.qty ? `Confirm ${fmt(qty)} · short` : `Confirm ${fmt(qty)} · ${nextStep ? "next line" : "done"}`} <I n="arrow" />
            </button>
            <button type="button" className="w-btn" onClick={() => setPhase("short")}>I picked fewer</button>
          </div>
        </>
      )}
      {phase === "short" && (
        <>
          <div className="w-body">
            <Alert tone="warn" title={`Picked ${fmt(qty)} of ${fmt(step.qty)}`}>{fmt(step.qty - qty)} {unit} short from {fromBin}</Alert>
            <div className="w-card w-qtycard">
              <label htmlFor="short-qty" className="w-label">How many did you pick?</label>
              <Stepper id="short-qty" value={qty} onChange={setQty} max={step.qty} of={`of ${fmt(step.qty)}`} />
            </div>
            <fieldset className="w-reasons">
              <legend className="w-label">Why is it short?</legend>
              {Object.entries(doc.shortReasons).map(([k, label]) => (
                <label key={k} className={reason === k ? "sel" : ""}><input type="radio" name="reason" checked={reason === k} onChange={() => setReason(k)} />{label}</label>
              ))}
            </fieldset>
            {reason === "empty_bin" || reason === "cant_find" ? <p className="w-small">This flags {fromBin} for a count.</p> : null}
            {err && <Alert title="Not saved">{err}</Alert>}
          </div>
          <div className="w-foot">
            <button type="button" className="w-btn primary" disabled={busy || qty >= step.qty} onClick={() => (qty > 0 && tracking && !lot && !serial && !step.lotNo && !step.serialNo ? (setErr(`Scan the ${tracking} first.`), setPhase("lot")) : save({ picked: qty, shortReason: reason }))}>
              Confirm {fmt(qty)} · next line
            </button>
            <button type="button" className="w-btn" onClick={() => setPhase(qty > 0 ? "qty" : "bin")}>Back</button>
          </div>
        </>
      )}
    </>
  );
}
